/**
 * The app's single dropdown. Native `<select>` renders its option list through
 * the OS, which no stylesheet can reach — the popup arrives with the platform's
 * own blue highlight and metrics, so two selects side by side never match the
 * rest of the product. This is the same listbox everywhere instead: a bordered
 * white panel (no shadow — DESIGN.md Elevation) whose current value carries a
 * right-aligned check.
 *
 * Two triggers, one popup:
 * - `boxed` — the ordinary form control, the shared field look.
 * - `quiet` — text + chevron, no box. Settings rows (DESIGN.md "Selects are
 *   quiet") and the studio preview switchers.
 *
 * Keyboard parity with the native control: Up/Down/Home/End move the highlight,
 * Enter or Space commits, Escape and outside click close, Tab closes without
 * committing, and focus returns to the trigger on close.
 *
 * NOT for table cells. The popup is absolutely positioned, so any `overflow-*`
 * ancestor clips it for the bottom rows (DESIGN.md Wide admin tables).
 */
import { useEffect, useId, useRef, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "./icons";
import { useDismissable } from "../../lib/use-dismissable";

import styles from "./Select.module.css";

export type SelectOption = { value: string; label: string };

export function Select({
  id,
  value,
  options,
  onChange,
  variant = "boxed",
  size = "md",
  disabled = false,
  className = "",
  ariaLabel,
}: {
  id?: string;
  value: string;
  options: SelectOption[];
  onChange: (next: string) => void;
  variant?: "boxed" | "quiet";
  /** `boxed` only: `sm` is the filter-bar height. */
  size?: "md" | "sm";
  disabled?: boolean;
  /** Extra classes for the trigger (width). */
  className?: string;
  ariaLabel?: string;
}) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const listboxId = `${baseId}-listbox`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selectedLabel =
    selectedIndex >= 0 ? options[selectedIndex].label : value;

  // Keep the highlighted option in view when the list is long enough to scroll.
  useEffect(() => {
    if (!open) return;
    document
      .getElementById(`${baseId}-option-${activeIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex, baseId]);

  const openList = () => {
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  // Outside click closes without refocus (the user clicked elsewhere on
  // purpose); Escape closes and returns focus to the trigger.
  useDismissable({
    open,
    onDismiss: () => setOpen(false),
    onEscape: close,
    pointer: [containerRef],
  });

  // Picking an option IS the commit; an unchanged value never fires a change.
  const commit = (next: string) => {
    close();
    if (next !== value) onChange(next);
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        e.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case "Enter":
      case " ": {
        e.preventDefault();
        const active = options[activeIndex];
        if (active) commit(active.value);
        break;
      }
      // Escape is handled by useDismissable, which also covers the case
      // where focus has left the trigger.
      case "Tab":
        setOpen(false);
        break;
    }
  };

  // `quiet` sizes to its label; `boxed` fills its field.
  const full = variant === "boxed";
  const triggerClass =
    variant === "boxed" && size === "sm" ? styles.boxedSm : styles[variant];

  return (
    <span
      ref={containerRef}
      className={full ? styles.wrapFull : styles.wrapInline}
    >
      <button
        ref={triggerRef}
        id={baseId}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={
          open ? `${baseId}-option-${activeIndex}` : undefined
        }
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onTriggerKeyDown}
        className={`${triggerClass} ${className}`}
      >
        {/* A zero-width space holds the line box when no option is selected,
            so a boxed trigger keeps an input's height. */}
        <span className={styles.label}>{selectedLabel || "​"}</span>
        <ChevronDownIcon
          className={`${styles.chevron} ${
            full ? styles.chevronInField : styles.chevronInline
          }`}
        />
      </button>
      {open && (
        <span
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          className={`${styles.listbox} ${
            full ? styles.listboxFull : styles.listboxInline
          }`}
        >
          {options.map((o, i) => (
            <button
              key={o.value}
              id={`${baseId}-option-${i}`}
              type="button"
              role="option"
              aria-selected={o.value === value}
              tabIndex={-1}
              onClick={() => commit(o.value)}
              className={`${styles.option} ${
                i === activeIndex ? styles.optionActive : ""
              }`}
            >
              <span
                className={
                  full ? styles.optionLabelFull : styles.optionLabelInline
                }
              >
                {o.label}
              </span>
              {o.value === value && <CheckIcon className={styles.check} />}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
